/**
 * [Phase 2b-D §6.2 R6] Step 6 - Keepers (Live Only).
 *
 * Wizard scope: binary toggle only.
 *   form.use_keepers: bool
 *
 * Actual keeper-assignment UI (search + assign value + list) lives in
 * DraftLobby (§6.5.1). This step just sets the league preference.
 *
 * Optional step: no validation gate.
 */
import { Info } from "lucide-react";

const Radio = ({ checked, onClick, label, sub, dataTestId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={dataTestId}
    className={`w-full text-left flex items-start gap-3 rounded-lg border-2 px-4 py-3 transition-all ${
      checked
        ? "border-[#FF6B00] bg-[#FF6B00]/10"
        : "border-[#252525] bg-[#1C1C1C] hover:border-[#FF6B00]/40"
    }`}
  >
    <span
      className={`mt-1 inline-block h-4 w-4 rounded-full border-2 shrink-0 ${
        checked ? "border-[#FF6B00] bg-[#FF6B00]" : "border-[#A0A0A0]"
      }`}
    />
    <span className="flex-1">
      <span className="block font-display font-bold text-base text-[#F5F5F5]">
        {label}
      </span>
      {sub && <span className="block font-body text-xs text-[#A0A0A0] mt-0.5">{sub}</span>}
    </span>
  </button>
);

export default function Step6Keepers({ form, update, isSnake = false }) {
  if (isSnake) {
    // [Phase 2c-D §6.1] Keepers are auction-only in Phase 2c. Show notice;
    // hide the toggle. CommissionerSetup.buildSettings forces use_keepers=false
    // for snake regardless of stored form state.
    return (
      <div data-testid="wizard-step-6-snake-notice" className="space-y-6">
        <header>
          <h2 className="font-display font-black text-3xl uppercase text-[#F5F5F5]">
            Keepers
          </h2>
        </header>
        <div className="flex gap-3 bg-[#FF6B00]/[0.06] border border-[#FF6B00]/30 rounded-xl px-4 py-4">
          <Info size={16} className="text-[#FF6B00] shrink-0 mt-1" />
          <div>
            <p className="font-display font-bold text-sm text-[#F5F5F5]">
              Keepers are auction-only for now.
            </p>
            <p className="font-body text-sm text-[#A0A0A0] mt-1">
              Snake leagues run without keepers in Phase 2c. Snake-with-keepers support is coming in a future phase. Click "Next" to continue.
            </p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="wizard-step-6" className="space-y-8">
      <header>
        <h2 className="font-display font-black text-3xl uppercase text-[#F5F5F5]">
          Keepers
        </h2>
        <p className="font-body text-sm text-[#A0A0A0] mt-1">
          Carry players over from a previous season at a fixed auction value.
        </p>
      </header>

      <section className="space-y-3">
        <p className="font-display font-bold text-sm uppercase tracking-wider text-[#A0A0A0]">
          Does your league use keepers?
        </p>
        <Radio
          checked={form.use_keepers === false}
          onClick={() => update({ use_keepers: false })}
          label="No"
          sub="Standard auction - every player is up for bid."
          dataTestId="wizard-step6-no"
        />
        <Radio
          checked={form.use_keepers === true}
          onClick={() => update({ use_keepers: true })}
          label="Yes"
          sub="Each manager retains a player at a pre-set $ amount."
          dataTestId="wizard-step6-yes"
        />
      </section>

      {form.use_keepers && (
        <div
          data-testid="wizard-step6-info-card"
          className="flex gap-3 bg-[#FF6B00]/10 border border-[#FF6B00]/30 rounded-xl px-4 py-3"
        >
          <Info size={16} className="text-[#FF6B00] shrink-0 mt-0.5" />
          <p className="font-body text-sm text-[#F5F5F5]">
            Keeper assignments happen in the Draft Lobby once your teams are seated.
            You'll be able to add and remove keepers from there before the draft launches.
          </p>
        </div>
      )}
    </div>
  );
}

export const validateStep6 = () => null;
